import React from 'react';
import { IconChevronLeft } from '../icons';
import { StatusPill } from './EntityCard';

// Phone-app shell pieces — `BBM Context for claude/mobile.md` (Jaylen's
// Walkthrough App + Crew Portal Showroom picks, 2026-09-15). Used by the
// estimator walkthrough app and the crew portal. Both apps are pinned
// LIGHT (`theme-fixed` on the root) — dark mode never reaches them.
//
//   <MobileScreen
//       header={<MobileHeader title sub status onBack right />}
//       action={<MobileActionBar><Button size="lg" full>…</Button></MobileActionBar>}
//       tabs={<MobileTabBar items value onChange />}
//   >
//       …scrolling body…
//   </MobileScreen>

// Full-height column: header, optional strip (StepBar / tabs), scrolling
// body, pinned action bar, bottom tab bar. Body gets 12px padding.
export function MobileScreen({ header, strip, action, tabs, children, bodyClassName = '', className = '' }) {
    return (
        <div className={`theme-fixed fixed inset-0 flex flex-col bg-slate-100 text-slate-900 ${className}`}>
            {header}
            {strip}
            <div className={`flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-3 space-y-3 ${bodyClassName}`}>
                {children}
            </div>
            {action}
            {tabs}
        </div>
    );
}

// Charcoal header (the desktop sidebar ink): 44px back, title + status
// pill, one subline, one right-hand slot (Call / refresh).
export function MobileHeader({ title, sub, status, onBack, right, className = '' }) {
    return (
        <div className={`flex-shrink-0 flex items-center gap-2 px-3 text-white bg-[#16181d] ${className}`}
            style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)', paddingBottom: 12 }}>
            {onBack && (
                <button type="button" onClick={onBack} aria-label="Back" className="w-11 h-11 -ml-2 flex items-center justify-center rounded-lg text-white/90 active:bg-white/10 flex-shrink-0">
                    <IconChevronLeft size={24} />
                </button>
            )}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="text-[16px] font-bold truncate leading-tight">{title}</div>
                    {status && <StatusPill label={status.label} color={status.color} className="!bg-white/15 !text-white flex-shrink-0" />}
                </div>
                {sub && <div className="text-[11.5px] text-white/70 truncate mt-0.5">{sub}</div>}
            </div>
            {right && <div className="flex items-center gap-1.5 flex-shrink-0">{right}</div>}
        </div>
    );
}

// Small translucent header action (Call, refresh, ✕).
export function MobileHeaderButton({ label, onClick, href, children, className = '' }) {
    const cls = `h-10 min-w-[40px] px-3 inline-flex items-center justify-center gap-1.5 rounded-lg bg-white/15 text-white text-[13px] font-bold active:bg-white/25 ${className}`;
    if (href) return <a href={href} aria-label={label} className={cls}>{children}</a>;
    return <button type="button" onClick={onClick} aria-label={label} className={cls}>{children}</button>;
}

// Bottom tab bar: items [{ key, label, icon }], 56px + safe area.
export function MobileTabBar({ items = [], value, onChange, className = '' }) {
    return (
        <nav className={`flex-shrink-0 flex bg-white border-t border-slate-200 px-1.5 pt-1.5 ${className}`}
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 10px)' }} aria-label="Sections">
            {items.map((it) => {
                const on = it.key === value;
                return (
                    <button key={it.key} type="button" onClick={() => onChange(it.key)} aria-current={on ? 'page' : undefined}
                        className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-lg text-[11px] font-bold ${on ? 'text-red-600' : 'text-slate-400 active:bg-slate-50'}`}>
                        <span className="h-6 flex items-center justify-center">{it.icon}</span>
                        <span>{it.label}</span>
                        {it.badge != null && it.badge !== 0 && <span className="absolute" />}
                    </button>
                );
            })}
        </nav>
    );
}

// Pinned action bar above the tab bar (or the bottom edge). Put ONE
// `<Button size="lg" full>` in it, optionally a status line before it.
export function MobileActionBar({ children, className = '' }) {
    return (
        <div className={`flex-shrink-0 bg-white border-t border-slate-200 px-3 pt-2.5 pb-2.5 flex items-center gap-2 ${className}`}>
            {children}
        </div>
    );
}

// Progress bar for a stepped flow: n segments, `value` filled (1-based).
export function StepBar({ count, value, className = '' }) {
    return (
        <div className={`flex-shrink-0 flex gap-1 px-3 pt-2.5 bg-slate-100 ${className}`} role="progressbar" aria-valuemin={1} aria-valuemax={count} aria-valuenow={value}>
            {Array.from({ length: count }).map((_, i) => (
                <span key={i} className={`flex-1 h-1 rounded-full ${i < value ? 'bg-red-600' : 'bg-slate-300'}`} />
            ))}
        </div>
    );
}

// Section card for phone bodies: 12px radius, 12px padding, uppercase label.
export function MobileCard({ title, children, className = '', right }) {
    return (
        <section className={`bg-white rounded-xl border border-slate-200 p-3 ${className}`}>
            {(title || right) && (
                <div className="flex items-center justify-between gap-2 mb-2">
                    {title && <h3 className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400">{title}</h3>}
                    {right}
                </div>
            )}
            {children}
        </section>
    );
}

// 48px phone input recipe (form-controls rule at phone size).
export const mobileInputCls = 'w-full h-12 border border-slate-300 rounded-lg px-3 text-[15px] bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 placeholder:text-slate-400';

export default { MobileScreen, MobileHeader, MobileHeaderButton, MobileTabBar, MobileActionBar, StepBar, MobileCard, mobileInputCls };
